import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  FaPlus, FaSearch, FaEdit, FaTrash, FaEye, FaSave, FaTimes,
  FaSort, FaSortUp, FaSortDown, FaChevronLeft, FaChevronRight,
  FaArchive, FaExchangeAlt, FaTag, FaUsers, FaCalendarAlt,
  FaDollarSign, FaKey, FaPalette, FaLayerGroup, FaFlag,
  FaLock, FaGlobe, FaBuilding, FaUser, FaLink, FaFilter
} from "react-icons/fa";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

/* ─── Inline Styles (matching CustomerManagement design) ─── */
const S = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .pm-wrap {
    font-family: 'DM Sans', sans-serif;
    background: #FFFFFF;
    min-height: 100vh;
    color: #1a1a1a;
    padding-top: 120px;
  }

  .pm-page {
    padding: 0 24px 40px;
    width: 100%;
    max-width: 100%;
    margin: 0;
  }

  /* ── Header ── */
  .pm-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 28px;
  }
  .pm-title {
    font-size: 26px;
    font-weight: 600;
    letter-spacing: -0.5px;
    color: #111;
    margin: 0;
  }
  .pm-subtitle {
    font-size: 13px;
    color: #888;
    margin: 4px 0 0;
    font-weight: 400;
  }
  .pm-actions {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  /* ── Buttons ── */
  .btn-ghost-pm {
    background: #fff;
    border: 1px solid #e2e2e2;
    color: #444;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-ghost-pm:hover { background: #f0f0ee; border-color: #ccc; }

  .btn-primary-pm {
    background: #1e3a5f;
    border: 1px solid #1e3a5f;
    color: #fff;
    padding: 8px 18px;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-primary-pm:hover { background: #162d4a; }
  .btn-primary-pm:disabled { opacity: 0.5; cursor: not-allowed; }

  .btn-danger-pm {
    background: #c0392b;
    border: 1px solid #c0392b;
    color: #fff;
    padding: 8px 18px;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-danger-pm:hover { background: #a93226; }

  .btn-warning-pm {
    background: #e67e22;
    border: 1px solid #e67e22;
    color: #fff;
    padding: 8px 18px;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .btn-warning-pm:hover { background: #cf6d17; }

  /* ── Stats Row ── */
  .pm-stats-row {
    background: #fff;
    border: 1px solid #e8e8e6;
    border-radius: 12px;
    padding: 18px 20px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 24px;
    flex-wrap: wrap;
  }
  .pm-stats-label {
    font-size: 13px;
    font-weight: 600;
    color: #555;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    white-space: nowrap;
  }
  .pm-stat-pills { display: flex; gap: 10px; flex-wrap: wrap; }
  .pm-stat-pill {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    border-radius: 20px;
    border: 1px solid #e2e2e2;
    background: #fafaf9;
    font-size: 13px;
    font-weight: 500;
    color: #555;
  }
  .pm-stat-pill.active-pill { background: #edf5ed; border-color: #c8e6c9; color: #356035; }
  .pm-stat-pill.archived-pill { background: #f5f0e8; border-color: #e0d5c0; color: #7a5a2a; }
  .pm-stat-pill.planning-pill { background: #eaf0f8; border-color: #c0d4e8; color: #2a4a7a; }
  .pm-stat-dot { width: 7px; height: 7px; border-radius: 50%; }

  /* ── Toolbar ── */
  .pm-toolbar {
    background: #fff;
    border: 1px solid #e8e8e6;
    border-radius: 12px;
    padding: 18px 20px;
    margin-bottom: 16px;
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;
  }
  .pm-search {
    flex: 1;
    min-width: 200px;
    position: relative;
  }
  .pm-search input {
    width: 100%;
    padding: 8px 12px 8px 36px;
    border: 1px solid #e2e2e2;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    background: #fafaf9;
    color: #222;
    outline: none;
    transition: border 0.15s;
    box-sizing: border-box;
  }
  .pm-search input:focus { border-color: #999; background: #fff; }
  .pm-search-icon {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    color: #aaa;
    font-size: 14px;
    pointer-events: none;
  }
  .pm-select {
    padding: 8px 12px;
    border: 1px solid #e2e2e2;
    border-radius: 8px;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    background: #fafaf9;
    color: #333;
    outline: none;
    cursor: pointer;
    transition: border 0.15s;
  }
  .pm-select:focus { border-color: #999; }

  /* ── Table Card ── */
  .pm-table-card {
    background: #fff;
    border: 1px solid #e8e8e6;
    border-radius: 12px;
    overflow: hidden;
  }
  .pm-table-wrap {
    overflow-x: auto;
    max-height: 58vh;
    overflow-y: auto;
  }
  table.pm-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  table.pm-table thead th {
    background: #fafaf9;
    border-bottom: 1px solid #e8e8e6;
    padding: 11px 16px;
    text-align: left;
    font-weight: 600;
    color: #666;
    font-size: 11.5px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 1;
    cursor: pointer;
    user-select: none;
    transition: background 0.12s;
  }
  table.pm-table thead th:hover { background: #f0f0ee; }
  .th-inner { display: flex; align-items: center; gap: 5px; }
  table.pm-table tbody tr {
    border-bottom: 1px solid #f0f0ee;
    transition: background 0.1s;
  }
  table.pm-table tbody tr:last-child { border-bottom: none; }
  table.pm-table tbody tr:hover { background: #fafaf9; }
  table.pm-table tbody td { padding: 11px 16px; color: #333; vertical-align: middle; }

  /* ── Cell Styles ── */
  .pm-code-badge {
    font-size: 12px;
    font-weight: 500;
    color: #1e3a5f;
    background: #e8f0f8;
    border-radius: 4px;
    padding: 2px 8px;
    font-family: 'DM Mono', monospace;
  }
  .pm-name-cell { display: flex; align-items: center; gap: 10px; }
  .pm-avatar {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    color: #fff;
    letter-spacing: -0.5px;
  }
  .pm-name-text { font-weight: 500; color: #111; }
  .pm-sub-text { font-size: 11.5px; color: #999; margin-top: 1px; }

  /* Status badge */
  .pm-status-badge {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    gap: 5px;
  }
  .pm-status-badge.planning { background: #eaf0f8; color: #2a4a7a; }
  .pm-status-badge.active { background: #edf5ed; color: #356035; }
  .pm-status-badge.on-hold { background: #fef5e7; color: #7a4a00; }
  .pm-status-badge.completed { background: #f0f8f0; color: #2a5a2a; }
  .pm-status-badge.archived { background: #f5f0e8; color: #7a5a2a; }
  .pm-status-badge.cancelled { background: #fdf0ee; color: #7a2a2a; }
  .pm-status-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }

  /* Priority chip */
  .pm-priority-chip {
    display: inline-flex;
    align-items: center;
    padding: 3px 9px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    gap: 4px;
  }
  .pm-priority-chip.low { background: #f0f8f0; color: #356035; }
  .pm-priority-chip.medium { background: #fef5e7; color: #7a4a00; }
  .pm-priority-chip.high { background: #fdf0ee; color: #7a2a2a; }
  .pm-priority-chip.critical { background: #f5e8e8; color: #5a0a0a; }

  /* Type chip */
  .pm-type-chip {
    display: inline-flex;
    align-items: center;
    padding: 3px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: uppercase;
  }
  .pm-type-chip.internal { background: #eaf0f8; color: #2a4a7a; }
  .pm-type-chip.client { background: #f5f0e8; color: #7a5a2a; }
  .pm-type-chip.research { background: #f0eaf8; color: #4a2a7a; }
  .pm-type-chip.maintenance { background: #edf5ed; color: #356035; }
  .pm-type-chip.compliance { background: #fdf0ee; color: #7a2a2a; }
  .pm-type-chip.other { background: #f5f5f3; color: #666; }

  /* Visibility chip */
  .pm-vis-chip {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 500;
    gap: 4px;
    color: #888;
    background: #f5f5f3;
  }
  .pm-vis-chip.private { color: #5a2a5a; background: #f5eaf5; }
  .pm-vis-chip.internal { color: #2a4a7a; background: #eaf0f8; }
  .pm-vis-chip.public { color: #356035; background: #edf5ed; }

  /* Row Actions */
  .row-action-pm {
    background: none;
    border: none;
    width: 30px;
    height: 30px;
    border-radius: 6px;
    font-size: 13px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s;
  }
  .row-view-pm { color: #555; }
  .row-view-pm:hover { background: #f0f0ee; }
  .row-edit-pm { color: #1e3a5f; }
  .row-edit-pm:hover { background: #eaf0f8; }
  .row-archive-pm { color: #e67e22; }
  .row-archive-pm:hover { background: #fef5e7; }
  .row-del-pm { color: #c0392b; }
  .row-del-pm:hover { background: #fdf0ee; }

  /* ── Empty ── */
  .pm-empty { padding: 56px; text-align: center; color: #aaa; font-size: 14px; }

  /* ── Footer / Pagination ── */
  .pm-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    border-top: 1px solid #f0f0ee;
    font-size: 12.5px;
    color: #888;
  }
  .pm-pagination { display: flex; gap: 4px; }
  .pg-btn-pm {
    background: #fff;
    border: 1px solid #e2e2e2;
    border-radius: 6px;
    padding: 5px 10px;
    font-size: 12px;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    color: #444;
    transition: all 0.12s;
  }
  .pg-btn-pm:hover:not(:disabled) { background: #f5f5f3; border-color: #bbb; }
  .pg-btn-pm.active { background: #1e3a5f; color: #fff; border-color: #1e3a5f; }
  .pg-btn-pm:disabled { opacity: 0.4; cursor: default; }

  /* ── Loading ── */
  .pm-loading { padding: 64px; text-align: center; color: #aaa; font-size: 14px; }
  .pm-spinner {
    display: inline-block;
    width: 20px; height: 20px;
    border: 2px solid #e2e2e2;
    border-top-color: #555;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    margin-right: 10px;
    vertical-align: middle;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ── Modal ── */
  .pm-overlay {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.35);
    backdrop-filter: blur(3px);
    z-index: 2000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    animation: fadeInPm 0.2s ease;
  }
  @keyframes fadeInPm { from { opacity: 0; } to { opacity: 1; } }

  .pm-modal {
    background: #fff;
    border-radius: 14px;
    width: 100%;
    max-width: 760px;
    box-shadow: 0 24px 60px rgba(0,0,0,0.15);
    overflow: hidden;
    animation: modalInPm 0.2s ease;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
  }
  .pm-modal.sm { max-width: 480px; }
  .pm-modal.lg { max-width: 880px; }
  @keyframes modalInPm {
    from { opacity: 0; transform: translateY(12px) scale(0.98); }
    to   { opacity: 1; transform: none; }
  }

  .pm-modal-header {
    padding: 20px 24px 16px;
    border-bottom: 1px solid #f0f0ee;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
  }
  .pm-modal-title { font-size: 16px; font-weight: 600; color: #111; margin: 0; }
  .pm-modal-sub { font-size: 12px; color: #888; margin-top: 2px; }
  .pm-modal-close {
    background: #f5f5f3;
    border: none;
    width: 28px; height: 28px;
    border-radius: 50%;
    font-size: 16px;
    cursor: pointer;
    color: #666;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.15s;
  }
  .pm-modal-close:hover { background: #eaeae8; }

  .pm-modal-body {
    flex: 1;
    overflow-y: auto;
    padding: 22px 24px;
  }
  .pm-modal-body::-webkit-scrollbar { width: 5px; }
  .pm-modal-body::-webkit-scrollbar-thumb { background: #ddd; border-radius: 10px; }

  .pm-modal-footer {
    padding: 14px 24px;
    border-top: 1px solid #f0f0ee;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    flex-shrink: 0;
  }

  /* ── Form Elements ── */
  .pm-section-title {
    font-size: 11.5px;
    font-weight: 600;
    color: #555;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    margin-bottom: 14px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0ee;
    display: flex;
    align-items: center;
    gap: 7px;
  }
  .pm-form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-bottom: 22px;
  }
  .pm-form-grid.cols-3 { grid-template-columns: 1fr 1fr 1fr; }
  .pm-field { display: flex; flex-direction: column; gap: 5px; }
  .pm-field.span2 { grid-column: span 2; }
  .pm-field.span3 { grid-column: span 3; }
  .pm-label {
    font-size: 12px;
    font-weight: 600;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .pm-label .req { color: #c0392b; margin-left: 3px; }
  .pm-input, .pm-textarea, .pm-fieldselect {
    width: 100%;
    padding: 9px 12px;
    border: 1px solid #e2e2e2;
    border-radius: 8px;
    font-size: 13.5px;
    font-family: 'DM Sans', sans-serif;
    color: #222;
    background: #fafaf9;
    outline: none;
    transition: border 0.15s, box-shadow 0.15s;
    box-sizing: border-box;
  }
  .pm-input::placeholder, .pm-textarea::placeholder { color: #bbb; }
  .pm-input:focus, .pm-textarea:focus, .pm-fieldselect:focus {
    border-color: #888;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(0,0,0,0.05);
  }
  .pm-input.readonly {
    background: #f0f0ee;
    color: #888;
    font-weight: 500;
    cursor: default;
    font-family: 'DM Mono', monospace;
    font-size: 13px;
    letter-spacing: 0.5px;
  }
  .pm-textarea { resize: vertical; min-height: 78px; }
  .pm-fieldselect {
    appearance: none;
    cursor: pointer;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23aaa' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 10px center;
    padding-right: 30px;
  }
  .pm-field-hint { font-size: 11px; color: #aaa; margin-top: 2px; }
  .pm-input-group { position: relative; }
  .pm-input-prefix {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    color: #aaa;
    font-size: 12px;
    pointer-events: none;
  }
  .pm-input.with-prefix { padding-left: 30px; }

  /* Color Swatch Row */
  .pm-color-row {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }
  .pm-color-swatch {
    width: 28px; height: 28px;
    border-radius: 6px;
    cursor: pointer;
    border: 2px solid transparent;
    transition: border-color 0.15s, transform 0.15s;
  }
  .pm-color-swatch:hover { transform: scale(1.15); }
  .pm-color-swatch.selected { border-color: #222; transform: scale(1.1); }
  .pm-color-input {
    border: 1px solid #e2e2e2;
    border-radius: 6px;
    padding: 4px 8px;
    font-size: 12px;
    font-family: 'DM Mono', monospace;
    width: 100px;
    outline: none;
    background: #fafaf9;
    color: #444;
  }
  .pm-color-input:focus { border-color: #888; }

  /* Tags */
  .pm-tags-input-wrap {
    border: 1px solid #e2e2e2;
    border-radius: 8px;
    background: #fafaf9;
    padding: 7px 10px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    transition: border 0.15s, box-shadow 0.15s;
    min-height: 40px;
    cursor: text;
  }
  .pm-tags-input-wrap:focus-within { border-color: #888; background: #fff; box-shadow: 0 0 0 3px rgba(0,0,0,0.05); }
  .pm-tag-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #e8f0f8;
    color: #1e3a5f;
    border: 1px solid #c8d8e8;
    border-radius: 20px;
    padding: 2px 9px;
    font-size: 12px;
    font-weight: 500;
  }
  .pm-tag-rm {
    background: none;
    border: none;
    cursor: pointer;
    color: #1e3a5f;
    padding: 0 1px;
    display: flex;
    align-items: center;
    font-size: 10px;
    opacity: 0.6;
    transition: opacity 0.15s;
  }
  .pm-tag-rm:hover { opacity: 1; }
  .pm-tags-text-input {
    border: none;
    outline: none;
    background: transparent;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    color: #222;
    flex: 1;
    min-width: 80px;
  }
  .pm-tags-text-input::placeholder { color: #bbb; }

  /* ── Confirm Modal ── */
  .pm-confirm-icon {
    width: 52px; height: 52px;
    border-radius: 14px;
    margin: 0 auto 16px;
    display: flex; align-items: center; justify-content: center;
    font-size: 22px;
  }
  .pm-confirm-icon.danger { background: #fdf0ee; color: #c0392b; }
  .pm-confirm-icon.warning { background: #fef5e7; color: #e67e22; }
  .pm-confirm-title { font-size: 17px; font-weight: 600; color: #111; text-align: center; margin-bottom: 8px; }
  .pm-confirm-msg { font-size: 13.5px; color: #888; text-align: center; line-height: 1.6; }

  /* ── View Detail ── */
  .pm-detail-block {
    padding: 9px 12px;
    background: #fafaf9;
    border-radius: 8px;
    border: 1px solid #e8e8e6;
    font-size: 13.5px;
    min-height: 38px;
    display: flex;
    align-items: center;
    color: #222;
  }
  .pm-detail-block.empty { color: #bbb; }
  .pm-detail-block.mono { font-family: 'DM Mono', monospace; font-size: 13px; }

  /* ── Responsive ── */
  @media (max-width: 768px) {
    .pm-page { padding: 0 16px 40px; }
    .pm-header { flex-direction: column; gap: 14px; }
    .pm-form-grid { grid-template-columns: 1fr; }
    .pm-form-grid.cols-3 { grid-template-columns: 1fr; }
    .pm-field.span2, .pm-field.span3 { grid-column: span 1; }
    .pm-toolbar { gap: 8px; }
  }
`;

/* ─── Constants ─────────────────────────────────────────── */
const STATUSES = ["Planning", "Active", "On Hold", "Completed", "Archived", "Cancelled"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const VISIBILITIES = ["Private", "Internal", "Public"];
const PROJECT_TYPES = ["Internal", "Client", "Research", "Maintenance", "Compliance", "Other"];
const INDUSTRIES = ["IT", "Construction", "Manufacturing", "Consulting", "Marketing", "Healthcare", "Finance", "Education", "Other"];
const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD"];
const ACCENT_COLORS = ["#1e3a5f","#2d6a4f","#6d3b7d","#a0522d","#b5451b","#1a5276","#117a65","#145a32","#4a235a","#784212"];
const PAGE_SIZE = 10;

/* ─── Helpers ─────────────────────────────────────────── */
const initials = (name = "") => name.slice(0, 2).toUpperCase() || "PJ";
const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const formatCurrency = (amt, cur) =>
  amt ? `${cur || ""} ${Number(amt).toLocaleString("en-IN")}` : "—";

/* ─── Blank Form ─────────────────────────────────────── */
const blankProject = () => ({
  name: "", key: "", description: "", icon: "📁", accentColor: "#1e3a5f",
  category: "", industry: "", projectType: "Internal", customer: "",
  owner: "", status: "Planning", priority: "Medium", visibility: "Private",
  startDate: "", endDate: "", estimatedBudget: "", budgetCurrency: "INR",
  referenceNumber: "", tags: []
});

/* ─── Sub-components ──────────────────────────────────── */
function SortIcon({ field, sortField, sortDir }) {
  if (sortField !== field) return <FaSort style={{ opacity: 0.25, fontSize: 10 }} />;
  return sortDir === "asc"
    ? <FaSortUp style={{ color: "#1e3a5f", fontSize: 10 }} />
    : <FaSortDown style={{ color: "#1e3a5f", fontSize: 10 }} />;
}

function StatusBadge({ status }) {
  const s = (status || "planning").toLowerCase().replace(" ", "-");
  return (
    <span className={`pm-status-badge ${s}`}>
      <span className="pm-status-dot" />
      {status || "Planning"}
    </span>
  );
}

function PriorityChip({ priority }) {
  const p = (priority || "medium").toLowerCase();
  return <span className={`pm-priority-chip ${p}`}>{priority || "Medium"}</span>;
}

function TypeChip({ type }) {
  const t = (type || "other").toLowerCase();
  return <span className={`pm-type-chip ${t}`}>{type || "—"}</span>;
}

function VisChip({ visibility }) {
  const v = (visibility || "private").toLowerCase();
  const Icon = v === "private" ? FaLock : v === "public" ? FaGlobe : FaBuilding;
  return (
    <span className={`pm-vis-chip ${v}`}>
      <Icon style={{ fontSize: 9 }} />
      {visibility || "Private"}
    </span>
  );
}

function TagInput({ tags = [], onChange }) {
  const [inp, setInp] = useState("");
  const addTag = (val) => {
    const t = val.trim();
    if (t && !tags.includes(t)) onChange([...tags, t]);
    setInp("");
  };
  return (
    <div className="pm-tags-input-wrap">
      {tags.map(t => (
        <span key={t} className="pm-tag-chip">
          {t}
          <button className="pm-tag-rm" onClick={() => onChange(tags.filter(x => x !== t))}>
            <FaTimes />
          </button>
        </span>
      ))}
      <input
        className="pm-tags-text-input"
        placeholder={tags.length ? "" : "Add tag, press Enter…"}
        value={inp}
        onChange={e => setInp(e.target.value)}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(inp); }
          if (e.key === "Backspace" && !inp && tags.length) onChange(tags.slice(0, -1));
        }}
        onBlur={() => inp.trim() && addTag(inp)}
      />
    </div>
  );
}

/* ─── Project Form Modal ──────────────────────────────── */
function ProjectModal({ project, customers, onClose, onSaved, token }) {
  const isEdit = !!project?.id;
  const [form, setForm] = useState(isEdit ? { ...blankProject(), ...project } : blankProject());
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.warning("Project name is required."); return; }
    if (!form.key.trim()) { toast.warning("Project key is required."); return; }
    if (!form.projectType) { toast.warning("Project type is required."); return; }
    if (!form.status) { toast.warning("Status is required."); return; }
    if (form.projectType === "Client" && !form.customer) {
      toast.warning("Customer is required for Client projects."); return;
    }
    if (form.endDate && form.startDate && form.endDate < form.startDate) {
      toast.warning("End date must be after start date."); return;
    }
    setSaving(true);
    try {
      const url = isEdit
        ? `${API_BASE_URL}/api/projects/${project.id}`
        : `${API_BASE_URL}/api/projects`;
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success(isEdit ? "Project updated!" : "Project created!");
      onSaved();
    } catch (err) {
      toast.error(err.message || (isEdit ? "Failed to update project." : "Failed to create project."));
    } finally { setSaving(false); }
  };

  return (
    <div className="pm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="pm-modal lg">
        <div className="pm-modal-header">
          <div>
            <div className="pm-modal-title">{isEdit ? "Edit Project" : "Add Project"}</div>
            <div className="pm-modal-sub">{isEdit ? `Editing ${project.name}` : "Fill in details to add a new project"}</div>
          </div>
          <button className="pm-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="pm-modal-body">
          {/* Basic Info */}
          <div className="pm-section-title"><FaLayerGroup /> Basic Information</div>
          <div className="pm-form-grid">
            <div className="pm-field">
              <label className="pm-label">Project Name <span className="req">*</span></label>
              <input className="pm-input" placeholder="e.g. HRMS Phase 2" value={form.name}
                onChange={e => set("name", e.target.value)} />
            </div>
            <div className="pm-field">
              <label className="pm-label">Project Key <span className="req">*</span></label>
              <div className="pm-input-group">
                <FaKey className="pm-input-prefix" />
                <input className="pm-input with-prefix"
                  placeholder="e.g. HRMS"
                  value={form.key}
                  onChange={e => set("key", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 10))}
                  readOnly={isEdit && project?.keyLocked}
                />
              </div>
              <span className="pm-field-hint">
                {isEdit && project?.keyLocked
                  ? "Key is locked after first task is linked"
                  : "Up to 10 chars, auto-generated from name — editable before first task"}
              </span>
            </div>
            <div className="pm-field span2">
              <label className="pm-label">Description</label>
              <textarea className="pm-textarea" placeholder="Rich text description of the project scope…"
                value={form.description} onChange={e => set("description", e.target.value)} />
            </div>
            <div className="pm-field">
              <label className="pm-label">Icon (Emoji)</label>
              <input className="pm-input" placeholder="e.g. 🚀" value={form.icon}
                onChange={e => set("icon", e.target.value)} maxLength={2} />
              <span className="pm-field-hint">Single emoji used as project avatar</span>
            </div>
            <div className="pm-field">
              <label className="pm-label">Accent Color</label>
              <div className="pm-color-row">
                {ACCENT_COLORS.map(c => (
                  <div key={c} className={`pm-color-swatch${form.accentColor === c ? " selected" : ""}`}
                    style={{ background: c }}
                    onClick={() => set("accentColor", c)} />
                ))}
                <input className="pm-color-input" placeholder="#3b82f6"
                  value={form.accentColor}
                  onChange={e => set("accentColor", e.target.value)} />
              </div>
            </div>
          </div>

          {/* Classification */}
          <div className="pm-section-title"><FaFilter /> Classification</div>
          <div className="pm-form-grid cols-3">
            <div className="pm-field">
              <label className="pm-label">Project Type <span className="req">*</span></label>
              <select className="pm-fieldselect" value={form.projectType}
                onChange={e => set("projectType", e.target.value)}>
                {PROJECT_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="pm-field">
              <label className="pm-label">Industry</label>
              <select className="pm-fieldselect" value={form.industry}
                onChange={e => set("industry", e.target.value)}>
                <option value="">Select industry…</option>
                {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
              </select>
            </div>
            <div className="pm-field">
              <label className="pm-label">Category</label>
              <input className="pm-input" placeholder="e.g. Engineering, Marketing"
                value={form.category} onChange={e => set("category", e.target.value)} />
            </div>
            <div className="pm-field">
              <label className="pm-label">
                Customer {form.projectType === "Client" && <span className="req">*</span>}
              </label>
              <select className="pm-fieldselect" value={form.customer}
                onChange={e => set("customer", e.target.value)}>
                <option value="">Select customer…</option>
                {(customers || []).map(c => (
                  <option key={c.id} value={c.id}>{c.customerName}</option>
                ))}
              </select>
              {form.projectType === "Client" && (
                <span className="pm-field-hint">Required when Project Type is Client</span>
              )}
            </div>
            <div className="pm-field">
              <label className="pm-label">Owner</label>
              <div className="pm-input-group">
                <FaUser className="pm-input-prefix" />
                <input className="pm-input with-prefix" placeholder="Assigned owner (user ID)"
                  value={form.owner} onChange={e => set("owner", e.target.value)} />
              </div>
              <span className="pm-field-hint">Defaults to creator if left blank</span>
            </div>
            <div className="pm-field">
              <label className="pm-label">Reference Number</label>
              <div className="pm-input-group">
                <FaLink className="pm-input-prefix" />
                <input className="pm-input with-prefix" placeholder="PO / Contract / Case no."
                  value={form.referenceNumber} onChange={e => set("referenceNumber", e.target.value)} />
              </div>
            </div>
          </div>

          {/* Status & Governance */}
          <div className="pm-section-title"><FaFlag /> Status & Governance</div>
          <div className="pm-form-grid cols-3">
            <div className="pm-field">
              <label className="pm-label">Status <span className="req">*</span></label>
              <select className="pm-fieldselect" value={form.status}
                onChange={e => set("status", e.target.value)}>
                {STATUSES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="pm-field">
              <label className="pm-label">Priority</label>
              <select className="pm-fieldselect" value={form.priority}
                onChange={e => set("priority", e.target.value)}>
                {PRIORITIES.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div className="pm-field">
              <label className="pm-label">Visibility</label>
              <select className="pm-fieldselect" value={form.visibility}
                onChange={e => set("visibility", e.target.value)}>
                {VISIBILITIES.map(v => <option key={v}>{v}</option>)}
              </select>
            </div>
          </div>

          {/* Timeline */}
          <div className="pm-section-title"><FaCalendarAlt /> Timeline</div>
          <div className="pm-form-grid">
            <div className="pm-field">
              <label className="pm-label">Start Date</label>
              <input className="pm-input" type="date" value={form.startDate}
                onChange={e => set("startDate", e.target.value)} />
            </div>
            <div className="pm-field">
              <label className="pm-label">End Date</label>
              <input className="pm-input" type="date" value={form.endDate}
                min={form.startDate || ""}
                onChange={e => set("endDate", e.target.value)} />
              <span className="pm-field-hint">Must be after start date</span>
            </div>
          </div>

          {/* Budget */}
          <div className="pm-section-title"><FaDollarSign /> Budget</div>
          <div className="pm-form-grid">
            <div className="pm-field">
              <label className="pm-label">Estimated Budget</label>
              <input className="pm-input" type="number" placeholder="0.00" step="0.01" min="0"
                value={form.estimatedBudget} onChange={e => set("estimatedBudget", e.target.value)} />
            </div>
            <div className="pm-field">
              <label className="pm-label">Budget Currency</label>
              <select className="pm-fieldselect" value={form.budgetCurrency}
                onChange={e => set("budgetCurrency", e.target.value)}>
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Tags */}
          <div className="pm-section-title"><FaTag /> Tags</div>
          <div className="pm-field" style={{ marginBottom: 4 }}>
            <TagInput tags={form.tags} onChange={v => set("tags", v)} />
            <span className="pm-field-hint" style={{ marginTop: 6 }}>Press Enter or comma to add tags</span>
          </div>
        </div>

        <div className="pm-modal-footer">
          <button className="btn-primary-pm" onClick={handleSave} disabled={saving}>
            <FaSave size={12} /> {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Project"}
          </button>
          <button className="btn-ghost-pm" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ─── View Project Modal ──────────────────────────────── */
function ViewProjectModal({ project, onClose, onEdit }) {
  if (!project) return null;
  const p = project;

  const Detail = ({ label, value, mono, children }) => (
    <div className="pm-field">
      <label className="pm-label">{label}</label>
      <div className={`pm-detail-block${!value && !children ? " empty" : ""}${mono ? " mono" : ""}`}>
        {children || value || "—"}
      </div>
    </div>
  );

  return (
    <div className="pm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="pm-modal lg">
        <div className="pm-modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div className="pm-avatar" style={{ background: p.accentColor || "#1e3a5f", width: 40, height: 40, fontSize: 18 }}>
              {p.icon || initials(p.name)}
            </div>
            <div>
              <div className="pm-modal-title">{p.name}</div>
              <div className="pm-modal-sub" style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4, flexWrap: "wrap" }}>
                {p.key && <span className="pm-code-badge">{p.key}</span>}
                <StatusBadge status={p.status} />
                <PriorityChip priority={p.priority} />
                <TypeChip type={p.projectType} />
                <VisChip visibility={p.visibility} />
              </div>
            </div>
          </div>
          <button className="pm-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="pm-modal-body">
          <div className="pm-section-title"><FaLayerGroup /> Basic Information</div>
          <div className="pm-form-grid">
            <Detail label="Project Name" value={p.name} />
            <Detail label="Project Key" value={p.key} mono />
            <div className="pm-field span2">
              <label className="pm-label">Description</label>
              <div className="pm-detail-block" style={{ alignItems: "flex-start", minHeight: 60, color: p.description ? "#222" : "#bbb" }}>
                {p.description || "—"}
              </div>
            </div>
            <Detail label="Icon" value={p.icon} />
            <div className="pm-field">
              <label className="pm-label">Accent Color</label>
              <div className="pm-detail-block" style={{ gap: 10 }}>
                <div style={{ width: 20, height: 20, borderRadius: 4, background: p.accentColor || "#1e3a5f" }} />
                <span style={{ fontFamily: "DM Mono, monospace", fontSize: 13, color: "#666" }}>{p.accentColor || "—"}</span>
              </div>
            </div>
          </div>

          <div className="pm-section-title"><FaFilter /> Classification</div>
          <div className="pm-form-grid cols-3">
            <Detail label="Project Type" value={p.projectType} />
            <Detail label="Industry" value={p.industry} />
            <Detail label="Category" value={p.category} />
            <Detail label="Customer" value={p.customerName || p.customer} />
            <Detail label="Owner" value={p.ownerName || p.owner} />
            <Detail label="Reference Number" value={p.referenceNumber} mono />
          </div>

          <div className="pm-section-title"><FaCalendarAlt /> Timeline & Budget</div>
          <div className="pm-form-grid cols-3">
            <Detail label="Start Date" value={formatDate(p.startDate)} />
            <Detail label="End Date" value={formatDate(p.endDate)} />
            <Detail label="Budget" value={formatCurrency(p.estimatedBudget, p.budgetCurrency)} />
          </div>

          {p.tags && p.tags.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <label className="pm-label" style={{ marginBottom: 8, display: "block" }}>Tags</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {p.tags.map(t => <span key={t} className="pm-tag-chip">{t}</span>)}
              </div>
            </div>
          )}

          <div style={{ marginTop: 16, fontSize: 12, color: "#aaa", textAlign: "right" }}>
            Created: {formatDate(p.createdAt)} · Updated: {formatDate(p.updatedAt)}
          </div>
        </div>

        <div className="pm-modal-footer">
          <button className="btn-ghost-pm" onClick={onClose}>Close</button>
          <button className="btn-primary-pm" onClick={() => { onClose(); onEdit(p); }}>
            <FaEdit size={12} /> Edit Project
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Confirm Modal ───────────────────────────────────── */
function ConfirmModal({ type, project, onClose, onConfirm }) {
  const isArchive = type === "archive";
  return (
    <div className="pm-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="pm-modal sm">
        <div className="pm-modal-body" style={{ padding: "36px 28px 24px", textAlign: "center" }}>
          <div className={`pm-confirm-icon ${isArchive ? "warning" : "danger"}`}>
            {isArchive ? <FaArchive /> : <FaTrash />}
          </div>
          <div className="pm-confirm-title">
            {isArchive ? "Archive Project?" : "Delete Project?"}
          </div>
          <div className="pm-confirm-msg">
            {isArchive
              ? <>Archiving <strong>{project?.name}</strong> will set it to read-only. All linked tasks, milestones, and documents are preserved. Archived projects are hidden from the active list.</>
              : <>Permanently deleting <strong>{project?.name}</strong> will unlink all tasks, milestones, documents, and member records. This action cannot be undone.</>
            }
          </div>
        </div>
        <div className="pm-modal-footer" style={{ justifyContent: "center" }}>
          <button className={isArchive ? "btn-warning-pm" : "btn-danger-pm"} onClick={onConfirm}>
            {isArchive ? <><FaArchive size={12} /> Archive</> : <><FaTrash size={12} /> Delete</>}
          </button>
          <button className="btn-ghost-pm" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ──────────────────────────────────── */
export default function ProjectCRUD() {
  const [projects, setProjects] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [viewProject, setViewProject] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [customers, setCustomers] = useState([]);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  /* ── Auth guard ── */
  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN")) { toast.error("Unauthorized."); navigate("/"); }
  }, [navigate, token]);

  /* ── Fetch customers for dropdown ── */
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/customers/all?status=Active&size=200`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(d => setCustomers(Array.isArray(d) ? d : d.content || []))
      .catch(() => {});
  }, [token]);

  /* ── Fetch projects ── */
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page - 1, size: PAGE_SIZE, sortField, sortDir,
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(typeFilter && { projectType: typeFilter }),
        ...(priorityFilter && { priority: priorityFilter }),
      });
      const res = await fetch(`${API_BASE_URL}/api/projects?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setProjects(Array.isArray(data) ? data : data.content || []);
      setTotal(data.totalElements ?? (Array.isArray(data) ? data.length : 0));
    } catch { toast.error("Failed to load projects."); } finally { setLoading(false); }
  }, [page, search, statusFilter, typeFilter, priorityFilter, sortField, sortDir, token]);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  /* ── Sort ── */
  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
    setPage(1);
  };

  /* ── Archive / Delete ── */
  const handleConfirm = async () => {
    const { type, project } = confirmAction;
    try {
      if (type === "archive") {
        const res = await fetch(`${API_BASE_URL}/api/projects/${project.id}`, {
          method: "PUT",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ ...project, status: "Archived" })
        });
        if (!res.ok) throw new Error();
        toast.success("Project archived.");
      } else {
        const res = await fetch(`${API_BASE_URL}/api/projects/${project.id}`, {
          method: "DELETE", headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error();
        toast.success("Project deleted.");
      }
      setConfirmAction(null); fetchProjects();
    } catch { toast.error(type === "archive" ? "Failed to archive." : "Cannot delete — project may be linked to active tasks."); }
  };

  /* ── Pagination ── */
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageNums = () => {
    const pages = [];
    for (let i = Math.max(1, page - 2); i <= Math.min(totalPages, page + 2); i++) pages.push(i);
    return pages;
  };

  /* ── Stats ── */
  const activeCount = projects.filter(p => p.status?.toLowerCase() === "active").length;
  const planningCount = projects.filter(p => p.status?.toLowerCase() === "planning").length;
  const archivedCount = projects.filter(p => p.status?.toLowerCase() === "archived").length;

  return (
    <>
      <style>{S}</style>
      <div style={{ position: "fixed", top: 0, left: 0, width: "100%", zIndex: 1000 }}>
        <AdminNavbar />
      </div>

      <div className="pm-wrap">
        <div className="pm-page">

          {/* ── Header ── */}
          <div className="pm-header">
            <div>
              <h1 className="pm-title">Project Management</h1>
              <p className="pm-subtitle">Create and manage all projects, timelines, and budgets from one place</p>
            </div>
            <div className="pm-actions">
              <button className="btn-ghost-pm" onClick={fetchProjects}>↻ Refresh</button>
              <button className="btn-primary-pm" onClick={() => { setEditProject(null); setShowForm(true); }}>
                <FaPlus size={11} /> New Project
              </button>
            </div>
          </div>

          {/* ── Stats Row ── */}
          <div className="pm-stats-row">
            <span className="pm-stats-label">Overview</span>
            <div className="pm-stat-pills">
              <div className="pm-stat-pill">
                <span className="pm-stat-dot" style={{ background: "#555" }} />
                {total} Total
              </div>
              <div className="pm-stat-pill active-pill">
                <span className="pm-stat-dot" style={{ background: "#356035" }} />
                {activeCount} Active
              </div>
              <div className="pm-stat-pill planning-pill">
                <span className="pm-stat-dot" style={{ background: "#2a4a7a" }} />
                {planningCount} Planning
              </div>
              <div className="pm-stat-pill archived-pill">
                <span className="pm-stat-dot" style={{ background: "#7a5a2a" }} />
                {archivedCount} Archived
              </div>
            </div>
          </div>

          {/* ── Toolbar ── */}
          <div className="pm-toolbar">
            <div className="pm-search">
              <FaSearch className="pm-search-icon" />
              <input placeholder="Search by name, key, or reference…"
                value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <select className="pm-select" value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setPage(1); }}>
              <option value="">All Statuses</option>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
            <select className="pm-select" value={typeFilter}
              onChange={e => { setTypeFilter(e.target.value); setPage(1); }}>
              <option value="">All Types</option>
              {PROJECT_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
            <select className="pm-select" value={priorityFilter}
              onChange={e => { setPriorityFilter(e.target.value); setPage(1); }}>
              <option value="">All Priorities</option>
              {PRIORITIES.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>

          {/* ── Table ── */}
          <div className="pm-table-card">
            <div className="pm-table-wrap">
              {loading ? (
                <div className="pm-loading"><span className="pm-spinner" />Loading projects…</div>
              ) : (
                <table className="pm-table">
                  <thead>
                    <tr>
                      <th style={{ width: 44 }}>Sr no</th>
                      <th onClick={() => handleSort("key")}>
                        <span className="th-inner">Key <SortIcon field="key" sortField={sortField} sortDir={sortDir} /></span>
                      </th>
                      <th onClick={() => handleSort("name")}>
                        <span className="th-inner">Project <SortIcon field="name" sortField={sortField} sortDir={sortDir} /></span>
                      </th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Visibility</th>
                      <th>Customer</th>
                      <th onClick={() => handleSort("startDate")}>
                        <span className="th-inner">Start <SortIcon field="startDate" sortField={sortField} sortDir={sortDir} /></span>
                      </th>
                      <th onClick={() => handleSort("endDate")}>
                        <span className="th-inner">End <SortIcon field="endDate" sortField={sortField} sortDir={sortDir} /></span>
                      </th>
                      <th>Budget</th>
                      <th onClick={() => handleSort("createdAt")}>
                        <span className="th-inner">Created <SortIcon field="createdAt" sortField={sortField} sortDir={sortDir} /></span>
                      </th>
                      <th style={{ width: 130, textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.length === 0 ? (
                      <tr>
                        <td colSpan={13}>
                          <div className="pm-empty">No projects found for the selected filters.</div>
                        </td>
                      </tr>
                    ) : projects.map((p, idx) => (
                      <tr key={p.id}>
                        <td style={{ color: "#aaa", fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                          {(page - 1) * PAGE_SIZE + idx + 1}
                        </td>
                        <td><span className="pm-code-badge">{p.key || "—"}</span></td>
                        <td>
                          <div className="pm-name-cell">
                            <div className="pm-avatar" style={{ background: p.accentColor || "#1e3a5f" }}>
                              {p.icon || initials(p.name)}
                            </div>
                            <div>
                              <div className="pm-name-text">{p.name}</div>
                              {p.category && <div className="pm-sub-text">{p.category}</div>}
                            </div>
                          </div>
                        </td>
                        <td><TypeChip type={p.projectType} /></td>
                        <td><StatusBadge status={p.status} /></td>
                        <td><PriorityChip priority={p.priority} /></td>
                        <td><VisChip visibility={p.visibility} /></td>
                        <td style={{ fontSize: 12.5, color: "#777" }}>{p.customerName || "—"}</td>
                        <td style={{ fontSize: 12, color: "#888", fontFamily: "'DM Mono', monospace" }}>
                          {formatDate(p.startDate)}
                        </td>
                        <td style={{ fontSize: 12, color: "#888", fontFamily: "'DM Mono', monospace" }}>
                          {formatDate(p.endDate)}
                        </td>
                        <td style={{ fontSize: 12, color: "#555", fontFamily: "'DM Mono', monospace" }}>
                          {p.estimatedBudget
                            ? `${p.budgetCurrency || ""} ${Number(p.estimatedBudget).toLocaleString("en-IN")}`
                            : "—"}
                        </td>
                        <td style={{ fontSize: 12, color: "#888", fontFamily: "'DM Mono', monospace" }}>
                          {formatDate(p.createdAt)}
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: 3, justifyContent: "center" }}>
                            <button className="row-action-pm row-view-pm" title="View Details"
                              onClick={() => setViewProject(p)}><FaEye /></button>
                            <button className="row-action-pm row-edit-pm" title="Edit"
                              onClick={() => { setEditProject(p); setShowForm(true); }}><FaEdit /></button>
                            {p.status?.toLowerCase() !== "archived" && (
                              <button className="row-action-pm row-archive-pm" title="Archive"
                                onClick={() => setConfirmAction({ type: "archive", project: p })}><FaArchive /></button>
                            )}
                            <button className="row-action-pm row-del-pm" title="Delete"
                              onClick={() => setConfirmAction({ type: "delete", project: p })}><FaTrash /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Pagination */}
            <div className="pm-footer">
              <span>{total} project{total !== 1 ? "s" : ""}</span>
              {totalPages > 1 && (
                <div className="pm-pagination">
                  <button className="pg-btn-pm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
                  {pageNums().map(n => (
                    <button key={n} className={`pg-btn-pm${page === n ? " active" : ""}`}
                      onClick={() => setPage(n)}>{n}</button>
                  ))}
                  <button className="pg-btn-pm" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next ›</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {viewProject && (
        <ViewProjectModal
          project={viewProject}
          onClose={() => setViewProject(null)}
          onEdit={(p) => { setEditProject(p); setShowForm(true); }}
        />
      )}
      {showForm && (
        <ProjectModal
          project={editProject}
          customers={customers}
          token={token}
          onClose={() => { setShowForm(false); setEditProject(null); }}
          onSaved={() => { setShowForm(false); setEditProject(null); fetchProjects(); }}
        />
      )}
      {confirmAction && (
        <ConfirmModal
          type={confirmAction.type}
          project={confirmAction.project}
          onClose={() => setConfirmAction(null)}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
}